import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/Toast';
import { qk } from '@/api/hooks/keys';
import { describeSriEstado } from '@/domain/status';
import { currentPushState, disablePush, enablePush, entitlementMessageKey, listenForeground, showLocalNotification, syncToken, type PushState } from './firebase';

/** App-level: refresh the token on start and react to foreground pushes. Mount once (AppShell). */
export function usePushRuntime() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  useEffect(() => {
    if (currentPushState() !== 'enabled') return;
    let off: (() => void) | undefined;
    let cancelled = false;
    syncToken().catch(() => undefined);
    listenForeground((p) => {
      if (p.type === 'factura_status') {
        void qc.invalidateQueries({ queryKey: qk.facturasAll });
        void qc.invalidateQueries({ queryKey: ['factura'] });
        void qc.invalidateQueries({ queryKey: qk.usage });
        const title = t('push.facturaTitle', { numero: p.numero });
        const body = describeSriEstado(p.estado);
        toast.info(title, body);
        void showLocalNotification(title, body, `/comprobantes?q=${encodeURIComponent(p.numero)}`, p.solicitudId || undefined);
      } else if (p.type === 'entitlement_changed') {
        // Plan changed (purchase, renewal, cancellation, expiry…): refresh Pro state everywhere.
        void qc.invalidateQueries({ queryKey: qk.usage });
        const key = entitlementMessageKey(p.event, p.plan);
        const title = t(`push.entitlement.${key}.title`);
        const body = t(`push.entitlement.${key}.body`);
        if (key === 'billingIssue' || key === 'ended') toast.error(title, body);
        else toast.success(title, body);
        void showLocalNotification(title, body, '/cuenta', 'entitlement_changed');
      } else if (p.type === 'certificate_validation') {
        void qc.invalidateQueries({ queryKey: qk.emisor });
        void qc.invalidateQueries({ queryKey: qk.emisorMeta });
        const title = t('cuenta.cert.title');
        const body = t(`cuenta.cert.validation.${p.status}`, { defaultValue: p.status });
        toast.info(title, body);
        void showLocalNotification(title, body, '/cuenta', 'certificate_validation');
      }
    })
      .then((u) => {
        if (cancelled) u();
        else off = u;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      off?.();
    };
  }, [qc, toast, t]);
}

/** Preferences toggle state. */
export function usePushSetting() {
  const [state, setState] = useState<PushState>(() => currentPushState());
  const [busy, setBusy] = useState(false);
  const enable = useCallback(async () => {
    setBusy(true);
    try {
      setState(await enablePush());
    } finally {
      setBusy(false);
    }
  }, []);
  const disable = useCallback(async () => {
    setBusy(true);
    try {
      await disablePush();
      setState(currentPushState());
    } finally {
      setBusy(false);
    }
  }, []);
  return { state, busy, enable, disable };
}
